// Agent Profile — drawer content. §3.7: "Agent Profile opens in a drawer, not a
// page." The shell owns the overlay stack (§11.5), so this is only the content;
// it is handed to `overlays.openDrawer` by OrganizationMap.
//
// The drawer shows the whole of the agent record and nothing beyond it. There is
// no utilization, spend or outcome row here, and that is deliberate rather than
// pending: correction 5 in the build instruction establishes those fields do not
// exist on the record. A row reading one would render permanently blank and
// would read as broken rather than as absent.
import React from "react";

import { PROFILE_FIELDS, readField, isBlank } from "./orgModel";

function renderValue(value) {
  if (Array.isArray(value)) {
    return (
      <ul className="ap-list">
        {value.map((item, i) => (
          <li key={i}>
            {typeof item === "string" ? item : JSON.stringify(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (value && typeof value === "object") {
    return <pre className="ap-pre">{JSON.stringify(value, null, 2)}</pre>;
  }
  return String(value);
}

export default function AgentProfile({ agent, onProposeChange }) {
  if (!agent) {
    return (
      <div className="ap" role="status">
        <div className="state state-empty">
          <div className="state-empty-title">No agent selected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ap">
      <header className="ap-head">
        <div className="label">Agent Profile</div>
        <h2 className="ap-title">
          {isBlank(agent.title) ? agent.agent_id : agent.title}
        </h2>
        {isBlank(agent.department) ? null : (
          <div className="ap-dept dim">{agent.department}</div>
        )}
      </header>

      <dl className="ap-fields">
        {PROFILE_FIELDS.map((field) => {
          const value = readField(agent, field.key);
          const blank = isBlank(value);
          return (
            <div
              key={field.key}
              className={`ap-field${field.wide ? " ap-field-wide" : ""}`}
            >
              <dt className="label">{field.label}</dt>
              <dd className={field.mono ? "num" : undefined}>
                {blank ? (
                  // Designed empty state, per field. R6: render the absence,
                  // never a plausible-looking substitute.
                  <span className="ap-blank dim" aria-label="not set">
                    —
                  </span>
                ) : (
                  renderValue(value)
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      <footer className="ap-foot">
        {/* §3.7: the ONLY mutating affordance on this surface. /api/organization
            also exposes materialize, activate, suspend, restore and retire —
            none of them belong here, and none of them are wired anywhere in this
            lane. Changing the organization is a conversation with the Brain, not
            a button on a map. */}
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => onProposeChange && onProposeChange(agent)}
        >
          Propose change to Brain
        </button>
      </footer>
    </div>
  );
}
